import { Paper, TextField, Typography, useTheme } from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import Autocomplete from '@material-ui/lab/Autocomplete'
import React from 'react'
import type {
    VendorSubPricingSummaryCrewMember,
    VendorSubPricingSummaryServiceRow,
    VendorSubPricingSummaryWorkspace,
} from '../../../models/VendorSubPricing/types'

/** Offset for tabs, padding, and global chrome — tweak if the column clips or leaves a gap. */
const SIDEBAR_HEIGHT_CALC = 'calc(100vh - 220px)'

interface Props {
    crew: VendorSubPricingSummaryCrewMember[]
    workspaces: VendorSubPricingSummaryWorkspace[]
    services: VendorSubPricingSummaryServiceRow[]
    selectedSubUserId: number | null
    selectedApartmentId: number | null
    selectedServiceId: number | null
    serviceCoverageHints: Record<number, string>
    onSelectSub: (userId: number | null) => void
    onSelectWorkspace: (apartmentId: number | null) => void
    onSelectService: (serviceId: number) => void
}

export const SubsPricingSidebar = (props: Props) => {
    const theme = useTheme()
    const {
        crew,
        workspaces,
        services,
        selectedSubUserId,
        selectedApartmentId,
        selectedServiceId,
        serviceCoverageHints,
        onSelectSub,
        onSelectWorkspace,
        onSelectService,
    } = props

    const selectedCrew:
        | VendorSubPricingSummaryCrewMember
        | undefined = crew.find(
        (c) => c.sub_vendor_user_id === selectedSubUserId,
    )
    const selectedWorkspaceRow:
        | VendorSubPricingSummaryWorkspace
        | undefined = workspaces.find(
        (w) => w.apartment_id === selectedApartmentId,
    )

    const autocompleteTextFieldProps = {
        variant: 'outlined' as const,
        margin: 'dense' as const,
        size: 'small' as const,
    }

    const scrollableServices = (
        <Paper
            variant="outlined"
            style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                padding: theme.spacing(1),
                overflow: 'hidden',
                alignSelf: 'stretch',
            }}
        >
            <Typography
                variant="subtitle2"
                color="textSecondary"
                style={{
                    marginBottom: theme.spacing(0.75),
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    flexShrink: 0,
                }}
            >
                Services
            </Typography>
            <div
                style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                }}
            >
                {services.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                        {selectedApartmentId == null
                            ? 'Select a property to see services.'
                            : 'No services enabled at this property.'}
                    </Typography>
                ) : (
                    services.map((s) => (
                        <Paper
                            key={s.service_id}
                            elevation={0}
                            onClick={() => onSelectService(s.service_id)}
                            style={{
                                padding: theme.spacing(1, 1.25),
                                marginBottom: theme.spacing(0.75),
                                borderRadius: 8,
                                cursor: 'pointer',
                                border:
                                    selectedServiceId === s.service_id
                                        ? `1px solid ${theme.palette.primary.main}`
                                        : `1px solid ${theme.palette.divider}`,
                                backgroundColor:
                                    selectedServiceId === s.service_id
                                        ? theme.palette.action.selected
                                        : undefined,
                            }}
                        >
                            <Typography variant="body2">{s.name}</Typography>
                            {(() => {
                                const secondary =
                                    [
                                        serviceCoverageHints[s.service_id],
                                        !s.has_sub_vendor_contract &&
                                            'No sub-contract rates',
                                    ]
                                        .filter(Boolean)
                                        .join(' · ') || ''
                                return secondary ? (
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        component="div"
                                        style={{
                                            marginTop: theme.spacing(0.25),
                                        }}
                                    >
                                        {secondary}
                                    </Typography>
                                ) : null
                            })()}
                        </Paper>
                    ))
                )}
            </div>
        </Paper>
    )

    return (
        <div
            style={{
                width: 260,
                minWidth: 240,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(1.5),
                height: SIDEBAR_HEIGHT_CALC,
                maxHeight: SIDEBAR_HEIGHT_CALC,
            }}
        >
            <Paper
                variant="outlined"
                style={{
                    flexShrink: 0,
                    padding: theme.spacing(1),
                }}
            >
                <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    style={{
                        marginBottom: theme.spacing(0.75),
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                    }}
                >
                    Subs
                </Typography>
                {crew.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                        No team members loaded.
                    </Typography>
                ) : (
                    <Autocomplete<
                        VendorSubPricingSummaryCrewMember,
                        false,
                        false,
                        false
                    >
                        options={crew}
                        value={selectedCrew ?? null}
                        getOptionLabel={(c) =>
                            c.name || `User ${c.sub_vendor_user_id}`
                        }
                        onChange={(_, value) => {
                            onSelectSub(value?.sub_vendor_user_id ?? null)
                        }}
                        renderOption={(c) => (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: theme.spacing(1),
                                    width: '100%',
                                }}
                            >
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="body2" noWrap>
                                        {c.name ||
                                            `User ${c.sub_vendor_user_id}`}
                                    </Typography>
                                    {!c.has_any_sub_vendor_contract && (
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            component="div"
                                        >
                                            No contracts on file
                                        </Typography>
                                    )}
                                </div>
                                {c.has_any_sub_vendor_contract ? (
                                    <CheckCircleIcon
                                        fontSize="small"
                                        htmlColor={theme.palette.success.main}
                                        titleAccess="Has sub-contract records"
                                    />
                                ) : null}
                            </div>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                {...autocompleteTextFieldProps}
                                placeholder="Search crew"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {selectedCrew?.has_any_sub_vendor_contract ? (
                                                <CheckCircleIcon
                                                    fontSize="small"
                                                    htmlColor={
                                                        theme.palette.success
                                                            .main
                                                    }
                                                    style={{
                                                        marginRight: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                    titleAccess="Has sub-contract records"
                                                />
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                )}
            </Paper>

            <Paper
                variant="outlined"
                style={{
                    flexShrink: 0,
                    padding: theme.spacing(1),
                }}
            >
                <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    style={{
                        marginBottom: theme.spacing(0.75),
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                    }}
                >
                    Workspace
                </Typography>
                {workspaces.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                        No linked properties.
                    </Typography>
                ) : (
                    <Autocomplete<
                        VendorSubPricingSummaryWorkspace,
                        false,
                        false,
                        false
                    >
                        options={workspaces}
                        value={selectedWorkspaceRow ?? null}
                        getOptionLabel={(w) => w.name}
                        onChange={(_, value) => {
                            onSelectWorkspace(value?.apartment_id ?? null)
                        }}
                        renderOption={(w) => (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: theme.spacing(1),
                                    width: '100%',
                                }}
                            >
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="body2" noWrap>
                                        {w.name}
                                    </Typography>
                                    {!w.has_sub_vendor_contract && (
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            component="div"
                                        >
                                            No contracts for crew
                                        </Typography>
                                    )}
                                </div>
                                {w.has_sub_vendor_contract ? (
                                    <CheckCircleIcon
                                        fontSize="small"
                                        htmlColor={theme.palette.success.main}
                                        titleAccess="Has sub-contract records"
                                    />
                                ) : null}
                            </div>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                {...autocompleteTextFieldProps}
                                placeholder="Search workspace"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {selectedWorkspaceRow?.has_sub_vendor_contract ? (
                                                <CheckCircleIcon
                                                    fontSize="small"
                                                    htmlColor={
                                                        theme.palette.success
                                                            .main
                                                    }
                                                    style={{
                                                        marginRight: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                    titleAccess="Has sub-contract records"
                                                />
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                )}
            </Paper>

            {scrollableServices}
        </div>
    )
}
